import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { cn } from '@goaltree/ui/lib/utils'
import type { OptimisticLocalStore } from 'convex/browser'
import { useMutation, useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import { ArrowUp, Pin, PinOff, SendHorizontal, Trash2, X } from 'lucide-react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { toastCoachError } from '@/components/coaching/coach-utils'
import { RolePill } from '@/components/coaching/role-pill'
import { timeAgo } from '@/components/groves/grove-utils'
import { UserAvatar } from '@/components/user-avatar'

type Note = FunctionReturnType<typeof api.coaching.notes>[number]
// Extra composer buttons (the coach's Assistant draft) that can fill the box.
export type ComposerTools = (fill: (text: string) => void) => ReactNode
const NOTE_MAX = 1000

function patchNotes(
  store: OptimisticLocalStore,
  linkId: Id<'coachLinks'>,
  fn: (notes: Note[]) => Note[],
) {
  for (const { args, value } of store.getAllQueries(api.coaching.notes)) {
    if (value && args.linkId === linkId) store.setQuery(api.coaching.notes, args, fn(value))
  }
}

export function useAddNote(fromCoach: boolean) {
  return useMutation(api.coaching.addNote).withOptimisticUpdate(
    (store, { linkId, goalId, body }) => {
      const text = body.trim()
      const value = store.getQuery(api.coaching.notes, { linkId, goalId })
      if (!value || !text) return
      store.setQuery(api.coaching.notes, { linkId, goalId }, [
        ...value,
        {
          _id: `optimistic-${crypto.randomUUID()}` as Id<'coachNotes'>,
          goalId,
          body: text,
          pinned: false,
          createdAt: Date.now(),
          fromCoach,
          mine: true,
        },
      ])
    },
  )
}

export function NoteComposer({
  linkId,
  goalId,
  fromCoach,
  placeholder,
  autoFocus,
  onSent,
  tools,
  className,
  boxed,
}: {
  boxed?: boolean
  linkId: Id<'coachLinks'>
  goalId: Id<'nodes'> | null
  fromCoach: boolean
  placeholder: string
  autoFocus?: boolean
  onSent?: () => void
  tools?: ComposerTools
  className?: string
}) {
  const { t } = useTranslation()
  const add = useAddNote(fromCoach)
  const [body, setBody] = useState('')
  const box = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    const el = box.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`
  }, [body])
  const send = () => {
    const text = body.trim()
    if (!text) return
    setBody('')
    onSent?.()
    void add({ linkId, goalId, body: text }).catch((err) => {
      setBody(text)
      toastCoachError(err, t)
    })
  }
  if (boxed)
    return (
      <div className={cn('rounded-[22px] border border-border/70 bg-card p-1.5', className)}>
        <textarea
          ref={box}
          value={body}
          autoFocus={autoFocus}
          rows={1}
          maxLength={NOTE_MAX}
          placeholder={placeholder}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              send()
            }
          }}
          className="block max-h-[120px] min-h-9 w-full resize-none overflow-y-auto bg-transparent px-2.5 pt-2 pb-1 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none"
        />
        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={send}
            disabled={!body.trim()}
            aria-label={t('coaching.notes.send')}
            className={cn(
              'flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors disabled:cursor-default',
              body.trim()
                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                : 'bg-muted text-muted-foreground',
            )}
          >
            <ArrowUp className="size-4" strokeWidth={2.25} />
          </button>
        </div>
      </div>
    )
  return (
    <div className={cn('flex items-end gap-2', className)}>
      <textarea
        ref={box}
        value={body}
        autoFocus={autoFocus}
        rows={1}
        maxLength={NOTE_MAX}
        placeholder={placeholder}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            send()
          }
        }}
        className="min-h-9 flex-1 resize-none rounded-xl border bg-background px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/30"
      />
      {tools?.(setBody)}
      <button
        type="button"
        onClick={send}
        disabled={!body.trim()}
        aria-label={t('coaching.notes.send')}
        className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-40"
      >
        <SendHorizontal className="size-4" />
      </button>
    </div>
  )
}

export function NoteBubble({
  note: n,
  viewerIsCoach,
  author,
  children,
}: {
  note: Pick<Note, 'body' | 'createdAt' | 'fromCoach' | 'pinned'>
  viewerIsCoach: boolean
  author: string
  children?: ReactNode
}) {
  const { t, i18n } = useTranslation()
  return (
    <li
      className={cn(
        'group rounded-xl border px-3 py-2.5',
        n.fromCoach
          ? viewerIsCoach
            ? 'border-primary/25 bg-primary/5'
            : 'border-coach/30 bg-coach/5'
          : 'bg-card',
      )}
    >
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className="font-semibold text-foreground">{author}</span>
        {n.fromCoach !== viewerIsCoach && <RolePill role={n.fromCoach ? 'coach' : 'client'} />}
        <span>{timeAgo(n.createdAt, i18n.language)}</span>
        {n.pinned && (
          <span
            className={cn(
              'inline-flex items-center gap-1 font-semibold',
              viewerIsCoach ? 'text-primary-text' : 'text-coach-text',
            )}
          >
            <Pin className="size-3" />
            {t('coaching.notes.focus')}
          </span>
        )}
        {children && (
          <span className="ml-auto flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100 [@media(pointer:coarse)]:opacity-100">
            {children}
          </span>
        )}
      </div>
      <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{n.body}</p>
    </li>
  )
}

// One thread (goal or general) between a coach and client. Coaches can pin a
// note as "This week's focus". Once coaching has ended the client reads it only.
export function NotesThread({
  linkId,
  goalId,
  viewerIsCoach,
  coachName,
  clientName,
  ended,
  composerTools,
  hideNoteId,
  className,
}: {
  linkId: Id<'coachLinks'>
  goalId: Id<'nodes'> | null
  viewerIsCoach: boolean
  coachName: string
  clientName: string
  ended?: boolean
  composerTools?: ComposerTools
  // Shown elsewhere (the focus card above the thread).
  hideNoteId?: Id<'coachNotes'>
  className?: string
}) {
  const { t } = useTranslation()
  const all = useQuery(api.coaching.notes, { linkId, goalId })
  const notes = hideNoteId ? all?.filter((n) => n._id !== hideNoteId) : all
  const pin = useMutation(api.coaching.pinNote).withOptimisticUpdate(
    (store, { noteId, pinned }) => {
      patchNotes(store, linkId, (ns) =>
        ns.map((n) =>
          n._id === noteId ? { ...n, pinned } : pinned && n.pinned ? { ...n, pinned: false } : n,
        ),
      )
      for (const { args, value } of store.getAllQueries(api.coaching.clientDetail)) {
        if (value?.linkId !== linkId) continue
        const note = all?.find((n) => n._id === noteId)
        store.setQuery(api.coaching.clientDetail, args, {
          ...value,
          pinned: pinned && note ? { ...note, pinned: true } : null,
        })
      }
    },
  )
  const remove = useMutation(api.coaching.deleteNote).withOptimisticUpdate((store, { noteId }) =>
    patchNotes(store, linkId, (ns) => ns.filter((n) => n._id !== noteId)),
  )

  return (
    <div className={cn('space-y-3', className)}>
      {notes === undefined ? (
        <div className="h-16 animate-pulse rounded-xl bg-muted/40" />
      ) : all?.length === 0 ? (
        <p className="py-2 text-sm text-muted-foreground">
          {viewerIsCoach ? t('coaching.notes.emptyCoach') : t('coaching.notes.empty')}
        </p>
      ) : (
        <ul className="space-y-2.5">
          {notes.map((n) => (
            <NoteBubble
              key={n._id}
              note={n}
              viewerIsCoach={viewerIsCoach}
              author={n.mine ? t('coaching.notes.you') : n.fromCoach ? coachName : clientName}
            >
              {viewerIsCoach && n.fromCoach && !n._id.startsWith('optimistic-') && (
                <button
                  type="button"
                  onClick={() => void pin({ noteId: n._id, pinned: !n.pinned }).catch(() => {})}
                  aria-label={n.pinned ? t('coaching.notes.unpin') : t('coaching.notes.pin')}
                  title={n.pinned ? t('coaching.notes.unpin') : t('coaching.notes.pin')}
                  className="cursor-pointer rounded p-1 hover:bg-muted hover:text-foreground"
                >
                  {n.pinned ? <PinOff className="size-3.5" /> : <Pin className="size-3.5" />}
                </button>
              )}
              {n.mine && !ended && !n._id.startsWith('optimistic-') && (
                <button
                  type="button"
                  onClick={() => void remove({ noteId: n._id }).catch(() => {})}
                  aria-label={t('common.delete')}
                  className="cursor-pointer rounded p-1 hover:bg-muted hover:text-foreground"
                >
                  <Trash2 className="size-3.5" />
                </button>
              )}
            </NoteBubble>
          ))}
        </ul>
      )}
      {ended ? (
        <p className="text-xs text-muted-foreground">{t('coaching.goal.endedHint')}</p>
      ) : (
        <NoteComposer
          linkId={linkId}
          goalId={goalId}
          fromCoach={viewerIsCoach}
          tools={composerTools}
          placeholder={
            viewerIsCoach
              ? t('coaching.notes.placeholderCoach', { name: clientName })
              : t('coaching.notes.placeholderClient', { name: coachName })
          }
        />
      )}
    </div>
  )
}

// Today: the coach's pinned focus (or latest note this week), with Reply.
export function CoachNoteCard({ className }: { className?: string }) {
  const { t, i18n } = useTranslation()
  const card = useQuery(api.coaching.todayNote)
  const [replying, setReplying] = useState(false)
  const [sent, setSent] = useState(false)
  const hide = useMutation(api.coaching.hideTodayNote).withOptimisticUpdate((store) =>
    store.setQuery(api.coaching.todayNote, {}, null),
  )
  if (!card) return null
  const { note, coach } = card
  return (
    <div className={cn('rounded-xl border border-coach/30 bg-coach/5 px-4 py-3', className)}>
      <div className="flex items-center gap-2">
        <UserAvatar
          image={coach.image}
          name={coach.name}
          size={24}
          className="size-6 shrink-0 rounded-full ring-2 ring-coach/60"
        />
        <div className="flex min-w-0 flex-1 items-center gap-1.5 text-xs text-muted-foreground">
          <span className="truncate font-semibold text-foreground">{coach.name}</span>
          <RolePill role="coach" />
          <span className="min-w-0 truncate">
            {'· '}
            {note.pinned ? (
              <span className="font-semibold text-coach-text">{t('coaching.notes.focus')}</span>
            ) : (
              (card.goalTitle ?? timeAgo(note.createdAt, i18n.language))
            )}
          </span>
        </div>
        <button
          type="button"
          onClick={() => void hide({ noteId: note._id }).catch(() => {})}
          aria-label={t('common.close')}
          className="cursor-pointer rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="size-3.5" />
        </button>
      </div>
      <p className="mt-1.5 whitespace-pre-wrap text-sm font-medium text-foreground">{note.body}</p>
      {replying ? (
        <NoteComposer
          linkId={card.linkId}
          goalId={note.goalId}
          fromCoach={false}
          autoFocus
          placeholder={t('coaching.notes.placeholderClient', { name: coach.name })}
          onSent={() => {
            setReplying(false)
            setSent(true)
          }}
          className="mt-2.5"
        />
      ) : (
        <button
          type="button"
          onClick={() => setReplying(true)}
          className="mt-2 cursor-pointer text-xs font-semibold text-coach-text hover:underline"
        >
          {sent ? t('coaching.notes.replied') : t('coaching.notes.reply')}
        </button>
      )}
    </div>
  )
}
